import {Component, OnDestroy} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Faq, FaqApiService, FaqCategory, FaqStatus, ReferenceBookType} from '@npck/af-frontend-api';
import {extractPlainText, LocalizationService, NgxEditorValidators, RouterService, rxSpinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {Editor, Toolbar} from 'ngx-editor';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DESCRIPTION_MAX_LENGTH} from '../../../../domain/constants';
import {FAQ_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {KnowledgeBaseTab} from '../../knowledge-base.component';

export const NGX_EDITOR_DEFAULT_TOOLBAR: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code'],
    ['ordered_list', 'bullet_list'],
    [{heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']}],
    ['link'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
];

@Component({
    selector: 'af-create-edit-faq',
    standalone: false,
    templateUrl: './create-edit-faq.component.html',
    styleUrl: './create-edit-faq.component.scss',
})
export class CreateEditFaqComponent implements OnDestroy {
    ReferenceBookType = ReferenceBookType;
    DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
    NGX_EDITOR_DEFAULT_TOOLBAR = NGX_EDITOR_DEFAULT_TOOLBAR;

    editor = new Editor();

    faqId = this.activatedRoute.snapshot.paramMap.get(FAQ_ID);
    isEdit = !!this.faqId;

    displayAuthor = '';
    displayCreatedDate: string | null = null;

    form: FormGroup<CreateEditFaqForm>;
    tabPathToTab: Record<string, KnowledgeBaseTab> = {[Path.FAQ]: 'tabFaq'};

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private faqApiService: FaqApiService,
        private snackbarService: SnackbarService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private localizationService: LocalizationService,
    ) {
        if (this.faqId) {
            this.faqApiService.getFaq(this.faqId)
                .pipe(rxSpinner('initial'))
                .subscribe(faq => this.buildForm(faq));
        } else {
            this.buildForm();
        }
    }

    breadcrumbCustomizer = (path: string): string => {
        if (path === this.faqId) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#EditFaq', {id: this.faqId});
        }
        return path;
    };

    ngOnDestroy(): void {
        this.editor.destroy();
    }

    save(): void {
        if (this.form.invalid) {
            return;
        }
        const body: Faq = {
            category: this.form.controls.category.value,
            question: this.form.controls.question.value,
            answer: JSON.stringify(this.form.controls.answer.value),
            status: this.form.controls.publish.value ? FaqStatus.PUBLISHED : FaqStatus.NEW,
        };
        if (this.isEdit) {
            this.faqApiService.updateFaq(this.faqId, body)
                .pipe(rxSpinner('save'))
                .subscribe(() => {
                    this.snackbarService.showSnackBar('i18n_af_snackBarFaqUpdated');
                    this.routerService.navigate(Path.KNOWLEDGE_BASE);
                });
        } else {
            this.faqApiService.createFaq(body)
                .pipe(rxSpinner('save'))
                .subscribe(() => {
                    this.snackbarService.showSnackBar('i18n_af_snackBarFaqCreated');
                    this.routerService.navigate(Path.KNOWLEDGE_BASE);
                });
        }
    }

    cancel(): void {
        this.routerService.navigate(Path.KNOWLEDGE_BASE);
    }

    ngxEditorLength(): number {
        return this.form.controls.answer?.value
            ? extractPlainText(this.form.controls.answer.value).replace(/\s+/g, ' ').trim().length
            : 0;
    }

    private buildForm(faq?: Faq): void {
        this.displayAuthor = faq?.createdBy ?? this.authHolderService.username ?? '';
        this.displayCreatedDate = faq?.createdDate ?? new Date().toISOString();
        this.form = this.formBuilder.group({
            category: new FormControl(faq?.category ?? null, Validators.required),
            question: new FormControl(faq?.question ?? '', [Validators.required, Validators.maxLength(250)]),
            answer: new FormControl(faq?.answer ? JSON.parse(faq.answer) : null,
                [
                    NgxEditorValidators.ngxEditorPlainTextRequiredValidator,
                    NgxEditorValidators.ngxEditorPlainTextMaxLengthValidator(DESCRIPTION_MAX_LENGTH),
                ],
            ),
            publish: new FormControl(faq?.status === FaqStatus.PUBLISHED),
        });
    }

}

interface CreateEditFaqForm {
    category: FormControl<FaqCategory>;
    question: FormControl<string>;
    answer: FormControl<object>;
    publish: FormControl<boolean>;
}
